import { useState, useEffect, useRef } from "react";
import { sqlRead, sqlEscape } from "../utils/sqlClient";

import { useIsAdmin, AdminGate } from "../auth/AdminContext";

// Vendors — admin directory of Vendors + their VendorContacts
// (db/40_vendors_admin.sql, on top of the Vendors/VendorContacts tables and
// sp_SaveVendor added by db/12_vendor_contacts.sql). Vendor rows are also
// created automatically from quote intake (sp_WriteQuote auto-captures the
// quote's vendor + contact) — this page is where an estimator cleans that up:
// merge-free editing of name/trade/website/notes, adding/removing contacts,
// and deactivating a vendor that's gone stale (soft-delete only, so history
// on old quotes stays intact — a deactivated vendor just stops appearing in
// the quote-intake vendor picker).
//
// Same edit workflow as Quote Log Setup / Condition Library: loads read-only,
// "Edit" unlocks every field/add/remove action, all of which stay purely
// local until "Save Changes" persists everything at once ("Cancel" discards
// and reloads). Unlike those two pages, there's no separate delete proc for
// contacts — sp_SaveVendor takes each vendor's FULL current contact list and
// soft-deletes anything omitted, so removing a contact locally is enough; it
// just won't be in the array sp_SaveVendor gets for that vendor at save time.

const ctl = { padding: "6px 8px", fontSize: 12.5, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", width: "100%", boxSizing: "border-box" };
const VENDOR_GRID  = "22px 22px minmax(160px,1fr) 110px 130px minmax(140px,1fr) 100px 70px";
const CONTACT_GRID = "minmax(120px,1fr) minmax(110px,1fr) minmax(160px,1fr) 120px 28px";

function ManufacturerChip({ name, editing, onRemove }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px 3px 10px",
      fontSize: 11.5, fontWeight: 600, background: "#eef2ff", color: "#3730a3",
      borderRadius: 100, marginRight: 6, marginBottom: 6,
    }}>
      {name}
      {editing && (
        <button
          onClick={onRemove}
          title="Remove manufacturer"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 12, color: "#3730a3", lineHeight: 1 }}
        >×</button>
      )}
    </span>
  );
}

function ManufacturerEditor({ names, editing, onAdd, onRemove }) {
  const [draft, setDraft] = useState("");
  function commit() {
    const v = draft.trim();
    if (v) onAdd(v);
    setDraft("");
  }
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {names.length === 0 && !editing && (
          <span style={{ fontSize: 11.5, color: "var(--text3)" }}>No manufacturers recorded.</span>
        )}
        {names.map(n => (
          <ManufacturerChip key={n.key} name={n.ManufacturerName} editing={editing} onRemove={() => onRemove(n)} />
        ))}
      </div>
      {editing && (
        <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
            placeholder="Add a manufacturer, e.g. Kenall"
            style={{ ...ctl, maxWidth: 220 }}
          />
          <AddButton small onClick={commit}>+ Add</AddButton>
        </div>
      )}
    </div>
  );
}

function Field({ editing, value, onCommit, type = "text", placeholder, mono }) {
  if (!editing) {
    return (
      <div style={{
        padding: "6px 8px", fontSize: 12.5, color: value ? "var(--text)" : "var(--text3)",
        fontFamily: mono ? "monospace" : undefined, whiteSpace: "nowrap", overflow: "hidden",
        textOverflow: "ellipsis",
      }}>
        {value || "—"}
      </div>
    );
  }
  return (
    <input
      defaultValue={value ?? ""}
      type={type}
      placeholder={placeholder}
      style={{ ...ctl, fontFamily: mono ? "monospace" : undefined }}
      onBlur={e => { if (e.target.value !== (value ?? "")) onCommit(e.target.value); }}
    />
  );
}

function DeleteButton({ onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title || "Remove"}
      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#d1d5db", flexShrink: 0 }}
      onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
      onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
    >🗑</button>
  );
}

function AddButton({ onClick, children, small }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: small ? "4px 10px" : "6px 14px", fontSize: small ? 11 : 12,
        fontWeight: 700, background: "var(--surface)", color: "var(--cs-blue)",
        border: "1px dashed var(--cs-blue)", borderRadius: 6, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function Triangle({ open, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={open ? "Collapse" : "Expand"}
      style={{
        background: "none", border: "none", cursor: "pointer", padding: 4,
        fontSize: 10, color: "var(--text3)", display: "flex", alignItems: "center", justifyContent: "center",
        transform: open ? "rotate(90deg)" : "none", transition: "transform 0.12s",
      }}
    >▸</button>
  );
}

function CountBadge({ count, noun = "contact" }) {
  const empty = count === 0;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 100,
      background: empty ? "#fef3c7" : "#eef2ff", color: empty ? "#92400e" : "#3730a3",
      whiteSpace: "nowrap", justifySelf: "start",
    }}>
      {empty ? `No ${noun}s` : `${count} ${noun}${count === 1 ? "" : "s"}`}
    </span>
  );
}

function ActiveToggle({ active, editing, onToggle }) {
  if (!editing) {
    return (
      <span style={{
        fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 100,
        background: active ? "#d1fae5" : "#f3f4f6", color: active ? "#065f46" : "var(--text3)",
      }}>
        {active ? "Active" : "Inactive"}
      </span>
    );
  }
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text2)", cursor: "pointer" }}>
      <input type="checkbox" checked={!!active} onChange={e => onToggle(e.target.checked)} />
      Active
    </label>
  );
}

function GridHeader({ template, labels }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: template, gap: 8, alignItems: "center",
      padding: "4px 6px", fontSize: 10, fontWeight: 700, color: "var(--text3)",
      textTransform: "uppercase", letterSpacing: "0.04em",
    }}>
      {labels.map((l, i) => <span key={i}>{l}</span>)}
    </div>
  );
}

function ContactRow({ row, editing, onUpdate, onRemove }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: CONTACT_GRID, gap: 8, alignItems: "center", marginBottom: 6 }}>
      <Field editing={editing} value={row.ContactName}  onCommit={v => onUpdate({ ContactName: v })}  placeholder="Name" />
      <Field editing={editing} value={row.ContactTitle} onCommit={v => onUpdate({ ContactTitle: v })} placeholder="Title" />
      <Field editing={editing} value={row.ContactEmail} onCommit={v => onUpdate({ ContactEmail: v })} placeholder="Email" />
      <Field editing={editing} value={row.ContactPhone} onCommit={v => onUpdate({ ContactPhone: v })} placeholder="Phone" />
      {editing ? <DeleteButton onClick={onRemove} title="Remove contact" /> : <span />}
    </div>
  );
}

function vendorKey(v) { return v.VendorID != null ? `id:${v.VendorID}` : `tmp:${v._tempKey}`; }

function MergeSelectCheckbox({ checked, disabled, onToggle }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={e => onToggle(e.target.checked)}
      title={disabled ? "Only two vendors can be merged at a time" : "Select for merge"}
      style={{ cursor: disabled ? "default" : "pointer" }}
    />
  );
}

// One field's two options, side by side — click a card to pick that vendor's
// value for this field on the merged record. "— empty —" is a legitimate,
// selectable choice (explicitly clears the field), not just a placeholder.
function FieldChoiceRow({ label, aValue, bValue, choice, onChoose }) {
  const identical = (aValue || "") === (bValue || "");
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
        {label}{identical && <span style={{ fontWeight: 400, textTransform: "none", marginLeft: 6 }}>(same on both)</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {["A", "B"].map(side => {
          const val = side === "A" ? aValue : bValue;
          const selected = choice === side;
          return (
            <div
              key={side}
              onClick={() => onChoose(side)}
              style={{
                padding: "7px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12.5,
                border: `1px solid ${selected ? "var(--cs-blue)" : "var(--cs-border)"}`,
                background: selected ? "rgba(37,99,235,0.06)" : "transparent",
                color: val ? "var(--text)" : "var(--text3)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
              title={val || "(empty)"}
            >
              {val || "— empty —"}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Detailed side-by-side comparison for the two currently-selected vendors.
// Whichever side wins the Vendor Name pick is the record that physically
// survives (its VendorID keeps every quote's history) — every other field is
// still independently pickable regardless of which side that is, and
// contacts from both sides are listed individually so a stale or wrong one
// can be excluded rather than only accepting the automatic email-based merge.
function MergeConfirmModal({ pair, fields, onChooseField, contactsA, contactsB, excludedContactIds, onToggleContact, manufacturersA, manufacturersB, excludedManufacturerIds, onToggleManufacturer, onCancel, onConfirm, merging }) {
  const [a, b] = pair;
  const allContacts = [
    ...contactsA.map(c => ({ ...c, _side: "A" })),
    ...contactsB.map(c => ({ ...c, _side: "B" })),
  ];
  const allManufacturers = [
    ...manufacturersA.map(m => ({ ...m, _side: "A" })),
    ...manufacturersB.map(m => ({ ...m, _side: "B" })),
  ];
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      padding: 16, boxSizing: "border-box",
    }}>
      <div style={{
        background: "var(--surface)", borderRadius: 12, width: "100%", maxWidth: 640,
        maxHeight: "90vh", overflowY: "auto", padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,0.25)", boxSizing: "border-box",
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "var(--text)" }}>Merge Vendors</div>
        <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "var(--text2)", lineHeight: 1.5 }}>
          Pick which vendor's data to keep, field by field. Whichever Vendor Name you choose becomes the surviving
          record — every quote, its full history, and the contacts you keep below move onto it. The other vendor is
          then deactivated (not deleted). This can't be easily undone.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)" }}>A · {a.VendorName}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)" }}>B · {b.VendorName}</div>
        </div>

        <FieldChoiceRow label="Vendor Name"  aValue={a.VendorName}   bValue={b.VendorName}   choice={fields.vendor_name}  onChoose={s => onChooseField("vendor_name", s)} />
        <FieldChoiceRow label="Trade"        aValue={a.Trade}        bValue={b.Trade}        choice={fields.trade}        onChoose={s => onChooseField("trade", s)} />
        <FieldChoiceRow label="Website"      aValue={a.Website}      bValue={b.Website}      choice={fields.website}      onChoose={s => onChooseField("website", s)} />
        <FieldChoiceRow label="Notes"        aValue={a.Notes}        bValue={b.Notes}        choice={fields.notes}        onChoose={s => onChooseField("notes", s)} />

        <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "14px 0 6px" }}>
          Manufacturers to keep
        </div>
        <p style={{ margin: "0 0 6px", fontSize: 11, color: "var(--text3)" }}>
          Both vendors' manufacturers are combined (not an either/or pick) — uncheck one to drop it.
        </p>
        {allManufacturers.length === 0 && (
          <div style={{ fontSize: 11.5, color: "var(--text3)" }}>Neither vendor has any manufacturers recorded.</div>
        )}
        {allManufacturers.map(m => {
          const key = m.VendorManufacturerID ?? m._tempKey;
          const excluded = excludedManufacturerIds.has(key);
          return (
            <label key={`${m._side}-${key}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12.5, color: excluded ? "var(--text3)" : "var(--text)", cursor: "pointer" }}>
              <input type="checkbox" checked={!excluded} onChange={e => onToggleManufacturer(key, !e.target.checked)} />
              <span style={{ fontWeight: 600 }}>{m.ManufacturerName}</span>
              <span style={{ fontSize: 10, color: "var(--text3)", marginLeft: "auto" }}>from {m._side === "A" ? a.VendorName : b.VendorName}</span>
            </label>
          );
        })}

        <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "14px 0 6px" }}>
          Contacts to keep
        </div>
        {allContacts.length === 0 && (
          <div style={{ fontSize: 11.5, color: "var(--text3)" }}>Neither vendor has any contacts.</div>
        )}
        {allContacts.map(c => {
          const key = c.ContactID ?? c._tempKey;
          const excluded = excludedContactIds.has(key);
          return (
            <label key={`${c._side}-${key}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12.5, color: excluded ? "var(--text3)" : "var(--text)", cursor: "pointer" }}>
              <input type="checkbox" checked={!excluded} onChange={e => onToggleContact(key, !e.target.checked)} />
              <span style={{ fontWeight: 600 }}>{c.ContactName || "(no name)"}</span>
              <span style={{ color: "var(--text3)" }}>{c.ContactEmail || ""}</span>
              <span style={{ fontSize: 10, color: "var(--text3)", marginLeft: "auto" }}>from {c._side === "A" ? a.VendorName : b.VendorName}</span>
            </label>
          );
        })}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <button
            onClick={onCancel}
            disabled={merging}
            style={{
              padding: "7px 16px", fontSize: 12.5, fontWeight: 600, background: "none",
              color: "var(--text2)", border: "1px solid var(--cs-border)", borderRadius: 6,
              cursor: merging ? "default" : "pointer",
            }}
          >Cancel</button>
          <button
            onClick={onConfirm}
            disabled={merging}
            style={{
              padding: "7px 16px", fontSize: 12.5, fontWeight: 700,
              background: merging ? "var(--cs-border)" : "#b45309", color: "#fff",
              border: "none", borderRadius: 6, cursor: merging ? "default" : "pointer",
            }}
          >{merging ? "Merging…" : "Merge"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Vendors() {
  const isAdmin = useIsAdmin();

  const [vendors, setVendors]   = useState([]);
  const [contacts, setContacts] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [openVendors, setOpenVendors]   = useState(new Set());
  const [mergeSelection, setMergeSelection] = useState(new Set()); // VendorIDs, max 2, edit-mode only
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeFields, setMergeFields]       = useState(null); // { vendor_name, trade, website, notes } → 'A' | 'B'
  const [mergeExcludedContacts, setMergeExcludedContacts] = useState(new Set()); // ContactIDs dropped from the merged result
  const [mergeExcludedManufacturers, setMergeExcludedManufacturers] = useState(new Set()); // VendorManufacturerIDs dropped from the merged result
  const [merging, setMerging]               = useState(false);
  const tempIdRef = useRef(0);
  const nextTempKey = () => (tempIdRef.current -= 1);

  const load = () => {
    setLoading(true);
    setError("");
    return Promise.all([
      sqlRead("EXEC sp_GetVendorsList"),
      sqlRead("EXEC sp_GetAllVendorContacts"),
      sqlRead("EXEC sp_GetAllVendorManufacturers"),
    ])
      .then(([v, c, m]) => { setVendors(v); setContacts(c); setManufacturers(m); })
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };
  // NOT useEffect(load, []) — load() returns a Promise, and an effect that
  // returns anything other than undefined/a function is treated as a cleanup
  // function by React (see the identical note in QuoteLogSetup.jsx, where
  // this shape of bug produced a real blank-page crash on navigation).
  useEffect(() => { load(); }, []);

  const toggleVendor = key => setOpenVendors(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // Selecting for merge is capped at two — a third click while two are already
  // selected is ignored (checkboxes on unselected rows are disabled at that point).
  function toggleMergeSelect(vendorId, checked) {
    setMergeSelection(prev => {
      const next = new Set(prev);
      if (checked) {
        if (next.size >= 2) return next;
        next.add(vendorId);
      } else {
        next.delete(vendorId);
      }
      return next;
    });
  }

  function updateVendor(row, patch) {
    setVendors(prev => prev.map(r => r === row ? { ...r, ...patch } : r));
  }

  function contactsFor(row) {
    return row.VendorID != null
      ? contacts.filter(c => c.VendorID === row.VendorID)
      : contacts.filter(c => c._vendorTempKey === row._tempKey);
  }

  function updateContact(row, patch) {
    setContacts(prev => prev.map(r => r === row ? { ...r, ...patch } : r));
  }

  function addContact(row) {
    const draft = row.VendorID != null
      ? { ContactID: null, VendorID: row.VendorID, _tempKey: nextTempKey(), ContactName: "", ContactTitle: "", ContactEmail: "", ContactPhone: "" }
      : { ContactID: null, VendorID: null, _vendorTempKey: row._tempKey, _tempKey: nextTempKey(), ContactName: "", ContactTitle: "", ContactEmail: "", ContactPhone: "" };
    setContacts(prev => [...prev, draft]);
    setOpenVendors(prev => new Set(prev).add(vendorKey(row)));
  }

  function removeContact(row) {
    setContacts(prev => prev.filter(c => c !== row));
  }

  // Manufacturers a vendor represents (Legrand carrying Kenall, Lithonia, etc.)
  // — VendorManufacturers, db/46_vendor_manufacturers.sql. `key` gives each row
  // a stable React key across either identity (real VendorManufacturerID or a
  // local temp key for an unsaved add), same shape as the contacts list.
  function manufacturersFor(row) {
    const rows = row.VendorID != null
      ? manufacturers.filter(m => m.VendorID === row.VendorID)
      : manufacturers.filter(m => m._vendorTempKey === row._tempKey);
    return rows.map(m => ({ ...m, key: m.VendorManufacturerID ?? m._tempKey }));
  }

  function addManufacturer(row, name) {
    const existing = manufacturersFor(row);
    if (existing.some(m => m.ManufacturerName.toLowerCase() === name.toLowerCase())) return;
    const draft = row.VendorID != null
      ? { VendorManufacturerID: null, VendorID: row.VendorID, _tempKey: nextTempKey(), ManufacturerName: name }
      : { VendorManufacturerID: null, VendorID: null, _vendorTempKey: row._tempKey, _tempKey: nextTempKey(), ManufacturerName: name };
    setManufacturers(prev => [...prev, draft]);
  }

  function removeManufacturer(entry) {
    setManufacturers(prev => prev.filter(m => (m.VendorManufacturerID ?? m._tempKey) !== entry.key));
  }

  function addVendor() {
    const draft = { VendorID: null, _tempKey: nextTempKey(), VendorName: "New Vendor", Trade: "", Website: "", Notes: "", Aliases: "", IsActive: true, ContactCount: 0, ManufacturerCount: 0 };
    setVendors(prev => [...prev, draft]);
    setOpenVendors(prev => new Set(prev).add(vendorKey(draft)));
  }

  // ── Save / Cancel ────────────────────────────────────────────────────────────
  async function saveVendorRow(row) {
    const payload = {
      vendor_id:    row.VendorID ?? null,
      vendor_name:  row.VendorName,
      trade:        row.Trade || null,
      website:      row.Website || null,
      notes:        row.Notes || null,
      aliases:      row.Aliases || null,
      is_active:    row.IsActive ? 1 : 0,
      manufacturers: manufacturersFor(row).map(m => m.ManufacturerName),
      contacts: contactsFor(row).map(c => ({
        contact_id:    c.ContactID ?? null,
        contact_name:  c.ContactName || null,
        contact_title: c.ContactTitle || null,
        contact_email: c.ContactEmail || null,
        contact_phone: c.ContactPhone || null,
      })),
    };
    return sqlRead(`EXEC sp_SaveVendor @payload='${sqlEscape(JSON.stringify(payload))}'`);
  }

  async function handleSaveAll() {
    setSaving(true);
    try {
      for (const row of vendors) {
        if (!row.VendorName?.trim()) continue;
        const result = await saveVendorRow(row);
        if (result?.[0]?.ErrorMessage) throw new Error(`${row.VendorName}: ${result[0].ErrorMessage}`);
      }
      await load();
      setEditMode(false);
      setMergeSelection(new Set());
    } catch (e) {
      alert("Error saving changes: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    load();
    setEditMode(false);
    setMergeSelection(new Set());
    setMergeModalOpen(false);
  }

  // ── Merge ────────────────────────────────────────────────────────────────────
  // Exactly two real (saved) vendors must be selected via their row checkboxes
  // before the "Merge Selected" button appears; the modal then compares every
  // field side by side. Whichever side wins the Vendor Name pick is the record
  // that physically survives (its VendorID is what quotes/contacts move onto).
  const mergePair = vendors.filter(v => mergeSelection.has(v.VendorID));

  // Default each field to whichever side actually has a value — if both do
  // (or neither does) default to A, but the estimator can always override.
  function defaultFieldSide(aVal, bVal) {
    if (aVal && !bVal) return "A";
    if (bVal && !aVal) return "B";
    return "A";
  }

  function openMergeModal() {
    const [a, b] = mergePair;
    setMergeFields({
      vendor_name:  defaultFieldSide(a.VendorName, b.VendorName),
      trade:        defaultFieldSide(a.Trade, b.Trade),
      website:      defaultFieldSide(a.Website, b.Website),
      notes:        defaultFieldSide(a.Notes, b.Notes),
    });
    setMergeExcludedContacts(new Set());
    setMergeExcludedManufacturers(new Set());
    setMergeModalOpen(true);
  }

  function chooseMergeField(field, side) {
    setMergeFields(prev => ({ ...prev, [field]: side }));
  }

  function toggleMergeContactExcluded(contactKey, excluded) {
    setMergeExcludedContacts(prev => {
      const next = new Set(prev);
      excluded ? next.add(contactKey) : next.delete(contactKey);
      return next;
    });
  }

  function toggleMergeManufacturerExcluded(manufacturerKey, excluded) {
    setMergeExcludedManufacturers(prev => {
      const next = new Set(prev);
      excluded ? next.add(manufacturerKey) : next.delete(manufacturerKey);
      return next;
    });
  }

  async function handleMergeConfirm() {
    const [a, b] = mergePair;
    if (!a || !b || !mergeFields) return;

    const sideVendor = side => (side === "A" ? a : b);
    const keep  = sideVendor(mergeFields.vendor_name);
    const merge = keep === a ? b : a;

    setMerging(true);
    try {
      const payload = {
        keep_vendor_id:  keep.VendorID,
        merge_vendor_id: merge.VendorID,
        fields: {
          vendor_name:  sideVendor(mergeFields.vendor_name).VendorName || "",
          trade:        sideVendor(mergeFields.trade).Trade || "",
          website:      sideVendor(mergeFields.website).Website || "",
          notes:        sideVendor(mergeFields.notes).Notes || "",
        },
        exclude_contact_ids:      [...mergeExcludedContacts],
        exclude_manufacturer_ids: [...mergeExcludedManufacturers],
      };
      const result = await sqlRead(`EXEC sp_MergeVendors @payload='${sqlEscape(JSON.stringify(payload))}'`);
      const row = result?.[0];
      if (!row?.Success) throw new Error(row?.ErrorMessage || "Merge failed");
      setMergeModalOpen(false);
      setMergeSelection(new Set());
      await load();
      alert(`Merged "${merge.VendorName}" into "${keep.VendorName}" — ${row.QuotesMoved} quote(s) and ${row.ContactsMoved} contact(s) moved.`);
    } catch (e) {
      alert("Error merging vendors: " + e.message);
    } finally {
      setMerging(false);
    }
  }

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;
  if (loading) return <div style={{ padding: 32, color: "var(--text3)", fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ padding: 32, color: "var(--red)", fontSize: 13 }}>{error}</div>;

  const q = search.trim().toLowerCase();
  const visibleVendors = vendors
    .filter(v => showInactive || v.IsActive || v.VendorID == null)
    .filter(v => !q || v.VendorName?.toLowerCase().includes(q) || v.Trade?.toLowerCase().includes(q))
    .sort((a, b) => (a.VendorName || "").localeCompare(b.VendorName || ""));

  // "Merge Selected" floats beside whichever selected row appears first in
  // the visible list, rather than sitting in a fixed toolbar spot — it should
  // read as attached to the vendors you just checked, not to the page chrome.
  const firstSelectedVendorId = visibleVendors.find(v => mergeSelection.has(v.VendorID))?.VendorID;

  return (
    <div style={{ padding: 32, maxWidth: 1080 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Vendors</h2>
          <p style={{ margin: "0 0 8px", fontSize: 13, color: "var(--text3)" }}>
            Vendor directory and their contacts — the same Vendors/VendorContacts data quote intake reads from and
            auto-populates. Edit here instead of only fixing it up quote-by-quote in Quote Search.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, paddingTop: 2 }}>
          {!editMode ? (
            <button onClick={() => setEditMode(true)} style={{
              padding: "7px 16px", fontSize: 12.5, fontWeight: 700, background: "var(--cs-blue)",
              color: "#fff", border: "none", borderRadius: 6, cursor: "pointer",
            }}>✏️ Edit</button>
          ) : (
            <>
              <button onClick={handleCancel} disabled={saving} style={{
                padding: "7px 16px", fontSize: 12.5, fontWeight: 600, background: "none",
                color: "var(--text2)", border: "1px solid var(--cs-border)", borderRadius: 6,
                cursor: saving ? "default" : "pointer",
              }}>Cancel</button>
              <button onClick={handleSaveAll} disabled={saving} style={{
                padding: "7px 16px", fontSize: 12.5, fontWeight: 700,
                background: saving ? "var(--cs-border)" : "var(--cs-blue)", color: "#fff",
                border: "none", borderRadius: 6, cursor: saving ? "default" : "pointer",
              }}>{saving ? "Saving…" : "💾 Save Changes"}</button>
            </>
          )}
        </div>
      </div>

      <p style={{ margin: "0 0 16px", fontSize: 12, color: "var(--text3)" }}>
        Expand a vendor to manage its contacts. Vendors are never hard-deleted — uncheck "Active" instead, which stops
        it from appearing in the quote-intake vendor picker while leaving its quote history and contacts intact.
        Click "Edit" to change anything; nothing is saved until you click "Save Changes". While editing, check two
        duplicate vendors and click "Merge Selected" to compare their data field by field and pick what the merged
        record keeps — every quote and its full history move to the one you keep, and merging auto-adds the old
        name as an alias so it can't come back as a fresh duplicate. Add aliases by hand too, for a vendor whose
        name shows up differently across quotes (an acronym, a former name).
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search vendor name or trade…"
          style={{ ...ctl, maxWidth: 280 }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text2)", cursor: "pointer", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
      </div>

      <div style={{ border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "#fff", padding: "6px 4px", overflow: "visible" }}>
        <GridHeader template={VENDOR_GRID} labels={["", "", "Vendor Name", "Trade", "Manufacturer", "Website", "Contacts", "Status"]} />
        {visibleVendors.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
            {vendors.length === 0 ? "No vendors yet." : "No vendors match your filters."}
          </div>
        )}
        {visibleVendors.map((v, vi) => {
          const key = vendorKey(v);
          const open = openVendors.has(key);
          const vContacts = contactsFor(v);
          const vManufacturers = manufacturersFor(v);
          return (
            <div key={key} style={{ position: "relative", borderTop: vi > 0 ? "1px solid var(--cs-border)" : "none", padding: "6px 6px", opacity: v.IsActive ? 1 : 0.65 }}>
              <div style={{ display: "grid", gridTemplateColumns: VENDOR_GRID, gap: 8, alignItems: "center" }}>
                {editMode && v.VendorID != null ? (
                  <MergeSelectCheckbox
                    checked={mergeSelection.has(v.VendorID)}
                    disabled={!mergeSelection.has(v.VendorID) && mergeSelection.size >= 2}
                    onToggle={checked => toggleMergeSelect(v.VendorID, checked)}
                  />
                ) : <span />}
                <Triangle open={open} onClick={() => toggleVendor(key)} />
                <Field editing={editMode} value={v.VendorName} onCommit={val => updateVendor(v, { VendorName: val })} />
                <Field editing={editMode} value={v.Trade} onCommit={val => updateVendor(v, { Trade: val })} placeholder="Trade" />
                <CountBadge count={vManufacturers.length} noun="manufacturer" />
                <Field editing={editMode} value={v.Website} onCommit={val => updateVendor(v, { Website: val })} placeholder="https://…" />
                <CountBadge count={vContacts.length} />
                <ActiveToggle active={v.IsActive} editing={editMode} onToggle={val => updateVendor(v, { IsActive: val })} />
              </div>

              {editMode && mergeSelection.size === 2 && v.VendorID === firstSelectedVendorId && (
                <button onClick={openMergeModal} style={{
                  position: "absolute", top: "50%", left: "100%", transform: "translateY(-50%)",
                  marginLeft: 10, padding: "6px 14px", fontSize: 12, fontWeight: 700, background: "#b45309",
                  color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap", zIndex: 5,
                }}>🔀 Merge Selected</button>
              )}

              {open && (
                <div style={{ marginTop: 8, marginLeft: 20, paddingLeft: 12, borderLeft: "2px solid var(--cs-border)" }}>
                  {editMode && (
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>
                        Notes
                      </label>
                      <textarea
                        defaultValue={v.Notes || ""}
                        rows={2}
                        placeholder="Notes"
                        style={{ ...ctl, resize: "vertical" }}
                        onBlur={e => { if (e.target.value !== (v.Notes || "")) updateVendor(v, { Notes: e.target.value }); }}
                      />
                    </div>
                  )}
                  {!editMode && v.Notes && (
                    <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 8, whiteSpace: "pre-wrap" }}>{v.Notes}</div>
                  )}

                  {editMode && (
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>
                        Aliases
                      </label>
                      <input
                        defaultValue={v.Aliases || ""}
                        placeholder="Comma-separated alternate names, e.g. ASI, ASI Doors Inc"
                        style={ctl}
                        onBlur={e => { if (e.target.value !== (v.Aliases || "")) updateVendor(v, { Aliases: e.target.value }); }}
                      />
                      <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
                        A future quote uploaded under any of these names routes to this vendor instead of creating a duplicate.
                      </div>
                    </div>
                  )}
                  {!editMode && v.Aliases && (
                    <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 8 }}>
                      Also known as: {v.Aliases}
                    </div>
                  )}

                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                    Manufacturers
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <ManufacturerEditor
                      names={vManufacturers}
                      editing={editMode}
                      onAdd={name => addManufacturer(v, name)}
                      onRemove={entry => removeManufacturer(entry)}
                    />
                  </div>

                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                    Contacts
                  </div>
                  {vContacts.length === 0 && (
                    <div style={{ fontSize: 11.5, color: "var(--text3)", padding: "4px 0 2px" }}>No contacts yet.</div>
                  )}
                  {vContacts.length > 0 && (
                    <GridHeader template={CONTACT_GRID} labels={["Name", "Title", "Email", "Phone", ""]} />
                  )}
                  {vContacts.map((c, ci) => (
                    <ContactRow
                      key={c.ContactID ?? c._tempKey ?? ci}
                      row={c}
                      editing={editMode}
                      onUpdate={patch => updateContact(c, patch)}
                      onRemove={() => removeContact(c)}
                    />
                  ))}
                  {editMode && (
                    <div style={{ marginTop: 4 }}>
                      <AddButton small onClick={() => addContact(v)}>+ Add Contact</AddButton>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {editMode && (
        <div style={{ marginTop: 12 }}>
          <AddButton onClick={addVendor}>+ Add Vendor</AddButton>
        </div>
      )}

      {mergeModalOpen && mergePair.length === 2 && mergeFields && (
        <MergeConfirmModal
          pair={mergePair}
          fields={mergeFields}
          onChooseField={chooseMergeField}
          contactsA={contactsFor(mergePair[0])}
          contactsB={contactsFor(mergePair[1])}
          excludedContactIds={mergeExcludedContacts}
          onToggleContact={toggleMergeContactExcluded}
          manufacturersA={manufacturersFor(mergePair[0])}
          manufacturersB={manufacturersFor(mergePair[1])}
          excludedManufacturerIds={mergeExcludedManufacturers}
          onToggleManufacturer={toggleMergeManufacturerExcluded}
          onCancel={() => setMergeModalOpen(false)}
          onConfirm={handleMergeConfirm}
          merging={merging}
        />
      )}
    </div>
  );
}
